import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Divider, Switch, FormControlLabel,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';

export default function ServerConfigContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [serverConfig, setServerConfig] = useState({ preferredPortalURL: '', fallbackPortalURLs: [], websiteServerURLs: [], clientApiBaseURL: '' });
  const [newFallbackURL, setNewFallbackURL] = useState('');
  const [newWebsiteServer, setNewWebsiteServer] = useState({ url: '', name: '', type: 'development', priority: 1, isActive: true });

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/config/bootstrap');
      if (response.data.success && response.data.config.serverConfig) {
        setServerConfig(response.data.config.serverConfig);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load server config', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/api/config/server-config', { serverConfig, updatedBy: 'admin' });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Server configuration updated', severity: 'success' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save server config', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const addFallbackURL = () => {
    if (newFallbackURL.trim()) {
      setServerConfig(prev => ({ ...prev, fallbackPortalURLs: [...(prev.fallbackPortalURLs || []), newFallbackURL.trim()] }));
      setNewFallbackURL('');
    }
  };

  const removeFallbackURL = (index) => {
    setServerConfig(prev => ({ ...prev, fallbackPortalURLs: prev.fallbackPortalURLs.filter((_, i) => i !== index) }));
  };

  const addWebsiteServer = () => {
    if (newWebsiteServer.url.trim() && newWebsiteServer.name.trim()) {
      setServerConfig(prev => ({ ...prev, websiteServerURLs: [...(prev.websiteServerURLs || []), { ...newWebsiteServer }] }));
      setNewWebsiteServer({ url: '', name: '', type: 'development', priority: 1, isActive: true });
    }
  };

  const removeWebsiteServer = (index) => {
    setServerConfig(prev => ({ ...prev, websiteServerURLs: prev.websiteServerURLs.filter((_, i) => i !== index) }));
  };

  const updateWebsiteServer = (index, field, value) => {
    setServerConfig(prev => ({
      ...prev,
      websiteServerURLs: prev.websiteServerURLs.map((server, i) => i === index ? { ...server, [field]: value } : server)
    }));
  };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Preferred Portal URL"
            value={serverConfig.preferredPortalURL}
            onChange={(e) => setServerConfig(prev => ({ ...prev, preferredPortalURL: e.target.value }))}
            helperText="Primary portal URL that iOS app will use"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Client API Base URL"
            value={serverConfig.clientApiBaseURL || ''}
            onChange={(e) => setServerConfig(prev => ({ ...prev, clientApiBaseURL: e.target.value }))}
            helperText="Base URL for iOS customer mode API calls (e.g. https://api.novosolution.org)"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>Fallback Portal URLs</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth label="Add Fallback URL" value={newFallbackURL} onChange={(e) => setNewFallbackURL(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addFallbackURL()} />
            <Button variant="contained" onClick={addFallbackURL}>Add</Button>
          </Box>
          {serverConfig.fallbackPortalURLs?.map((url, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField fullWidth value={url} disabled />
              <Button color="error" onClick={() => removeFallbackURL(index)}>Remove</Button>
            </Box>
          ))}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>Website Backend Servers</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>Add New Server</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Server URL" value={newWebsiteServer.url} onChange={(e) => setNewWebsiteServer(prev => ({ ...prev, url: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Server Name" value={newWebsiteServer.name} onChange={(e) => setNewWebsiteServer(prev => ({ ...prev, name: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth select label="Type" value={newWebsiteServer.type} onChange={(e) => setNewWebsiteServer(prev => ({ ...prev, type: e.target.value }))} SelectProps={{ native: true }}>
                  <option value="primary">Primary</option>
                  <option value="backup">Backup</option>
                  <option value="development">Development</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Priority" value={newWebsiteServer.priority} onChange={(e) => setNewWebsiteServer(prev => ({ ...prev, priority: parseInt(e.target.value) }))} /></Grid>
              <Grid item xs={12} sm={4}><Button fullWidth variant="contained" onClick={addWebsiteServer} sx={{ height: '56px' }}>Add Server</Button></Grid>
            </Grid>
          </Paper>

          {serverConfig.websiteServerURLs?.map((server, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}><TextField fullWidth label="URL" value={server.url} onChange={(e) => updateWebsiteServer(index, 'url', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Name" value={server.name} onChange={(e) => updateWebsiteServer(index, 'name', e.target.value)} /></Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth select label="Type" value={server.type} onChange={(e) => updateWebsiteServer(index, 'type', e.target.value)} SelectProps={{ native: true }}>
                    <option value="primary">Primary</option>
                    <option value="backup">Backup</option>
                    <option value="development">Development</option>
                  </TextField>
                </Grid>
                <Grid item xs={3} sm={1}><FormControlLabel control={<Switch checked={server.isActive} onChange={(e) => updateWebsiteServer(index, 'isActive', e.target.checked)} />} label="Active" /></Grid>
                <Grid item xs={3} sm={2}><Button fullWidth color="error" variant="outlined" onClick={() => removeWebsiteServer(index)}>Remove</Button></Grid>
              </Grid>
            </Paper>
          ))}
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={24} /> : 'Save Server Configuration'}</Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
}

